import clsx from 'clsx';
import Badge from '../ui/Badge';
import { formatCurrency } from '../../lib/formatPlan';

function formatDateOnly(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

const TYPE_META = {
  principal: { label: 'Principal', tone: 'info', dot: 'bg-info' },
  interest: { label: 'Interest', tone: 'success', dot: 'bg-accent' },
};

/**
 * v1.16.0 — payout history rendered as a date-ordered timeline rather than
 * a flat table, so an admin can visually spot gaps in a recording history
 * (several skipped months followed by one lump-sum catch-up payout look
 * very different in a timeline than they do as an aggregate "fully paid"
 * figure). Server already orders by paid_at descending; rendered as-is,
 * no client-side re-sort.
 *
 * Each row trusts the payout's own `type` field for its label and which
 * amount (principal vs interest) to show — never inferred from which of
 * the two stored amounts is non-zero.
 */
export default function PayoutTimeline({ payouts }) {
  if (!payouts || payouts.length === 0) {
    return <p className="py-6 text-center text-foreground-muted">No payouts recorded yet.</p>;
  }

  return (
    <ol className="relative border-l border-border ml-2 space-y-6 py-1">
      {payouts.map((p) => {
        const meta = TYPE_META[p.type] || { label: p.type || 'Unknown', tone: 'neutral', dot: 'bg-foreground-subtle' };
        const amount = p.type === 'principal' ? p.principal : p.type === 'interest' ? p.interest : null;
        return (
          <li key={p.id} className="relative ml-5">
            <span className={clsx('absolute -left-[27px] top-1 h-2.5 w-2.5 rounded-full ring-4 ring-background', meta.dot)} />
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={meta.tone}>{meta.label}</Badge>
              <span className="text-sm font-semibold text-foreground tabular-nums">
                {amount != null ? formatCurrency(amount) : '—'}
              </span>
              <span className="text-xs text-foreground-subtle">{formatDateOnly(p.paidAt)}</span>
            </div>
            {p.note && <p className="text-xs text-foreground-muted mt-1">{p.note}</p>}
            {p.recordedByName && <p className="text-[11px] text-foreground-subtle mt-0.5">Recorded by {p.recordedByName}</p>}
          </li>
        );
      })}
    </ol>
  );
}
